import { useEffect, useMemo, useState, useCallback } from 'react';
import type { ColDef } from 'ag-grid-community';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ListPageLayout } from '@/components/layout/list-page-layout';
import { useResourceNavigation, useGo } from '@/hooks/useResourceNavigation';
import {
	ActiveInactiveCellRenderer,
	DeleteCellRenderer
} from '@/components/grid';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { showMutationSuccessToast } from '@/lib/mutationRefetch';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { showDeleteColumn } from '@/lib/featureFlags';
import { toast } from 'sonner';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';

import {
	CONTRACTS_LIST_QUERY,
	DELETE_CONTRACT_MUTATION
} from './contracts.gql.tsx';
import { ContractDetailsQuery } from './contract-details-query';
import { ContractDetailsHeader } from './contract-details';
import {
	computeContractStatus,
	formatCurrency,
	formatDateRange
} from './utils';
import { ContractListItem } from './types';
import { selectContractAfterSave } from './contract-list-selection';

interface ContractNode {
	contractId: string;
	contractNumber?: string | null;
	beginDate: string;
	endDate: string;
	amount?: number | null;
	isAutoRenewed: boolean;
	simultaneousVehicleLimit?: number | null;
	reservedParkingSlots?: number | null;
	isActive: boolean;
	preferredLanguage?: string | null;
	includeAllCustomerVehicles: boolean;
	customer?: {
		customerId: string;
		firmName?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		isCompany?: boolean | null;
		customerCategory?: {
			description?: string | null;
			simultaneousVehicleLimit?: number | null;
		} | null;
	} | null;
	vehicles?: Array<{
		contractId: string;
		vehicleId: string;
		vehicle?: {
			vehicleId: string;
			plateNumber?: string | null;
			make?: string | null;
			model?: string | null;
			color?: string | null;
		} | null;
	}> | null;
	vipVehicles?: string[] | null;
	cards?: Array<{
		contractCardId?: string | null;
		facilityId: string;
		cardType: 'RFID' | 'BARCODE' | 'MAGNETIC';
		cardKey?: string | null;
		cardStatusId: string;
		depositTransactionId?: string | null;
		returnTransactionId?: string | null;
	}> | null;
	parkingSessions?: Array<{
		parkingSessionId: string;
		entryDateTime: string;
		exitDateTime?: string | null;
		status: string;
	}> | null;
}

interface ContractsListQueryResponse {
	contracts?: {
		nodes?: ContractNode[] | null;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		} | null;
	} | null;
}

const mapNodeToListItem = (node: ContractNode): ContractListItem => ({
	contractId: node.contractId,
	contractNumber: node.contractNumber ?? null,
	customer: {
		customerId: node.customer?.customerId ?? 'unknown-customer',
		firmName: node.customer?.firmName ?? '—',
		firstName: node.customer?.firstName ?? null,
		lastName: node.customer?.lastName ?? null,
		isCompany: node.customer?.isCompany ?? null,
		categoryName: node.customer?.customerCategory?.description ?? undefined,
		categoryLimit:
			node.customer?.customerCategory?.simultaneousVehicleLimit ??
			undefined,
		contactName: undefined,
		contactEmail: undefined,
		contactPhone: undefined
	},
	beginDate: node.beginDate,
	endDate: node.endDate,
	amount: (() => {
		if (typeof node.amount === 'number') {
			return node.amount;
		}
		if (typeof node.amount === 'string') {
			const parsed = Number(node.amount);
			return Number.isNaN(parsed) ? null : parsed;
		}
		return null;
	})(),
	billingCycle: undefined, // Not fetched in list query
	isAutoRenewed: node.isAutoRenewed,
	simultaneousVehicleLimit: node.simultaneousVehicleLimit ?? undefined,
	reservedParkingSlots: node.reservedParkingSlots ?? undefined,
	isActive: node.isActive,
	preferredLanguage: node.preferredLanguage ?? undefined,
	includeAllCustomerVehicles: node.includeAllCustomerVehicles ?? true,
	vehicles:
		node.vehicles?.map(vehicle => ({
			vehicleId: vehicle.vehicleId,
			plateNumber:
				vehicle.vehicle?.plateNumber ?? vehicle.vehicleId ?? '—',
			make: vehicle.vehicle?.make ?? undefined,
			model: vehicle.vehicle?.model ?? undefined,
			color: vehicle.vehicle?.color ?? undefined
		})) ?? [],
	vipVehicleIds: node.vipVehicles ?? [],
	vipVehicles: [], // Resolved in ContractDetailsQuery for the details panel
	cards:
		node.cards?.map(card => ({
			contractCardId: card.contractCardId ?? undefined,
			facilityId: card.facilityId,
			cardType: card.cardType,
			cardKey: card.cardKey ?? undefined,
			cardStatusId: card.cardStatusId,
			depositTransactionId: card.depositTransactionId ?? undefined,
			returnTransactionId: card.returnTransactionId ?? undefined,
		})) ?? [],
	metrics: {
		activeParkingSessions:
			node.parkingSessions?.filter(session => session.status === 'INSIDE')
				.length ?? 0,
		upcomingInvoiceDate: null,
		overstayIncidents: 0,
		lastAuditEvent: null
	},
	notes: [],
	status: computeContractStatus({
		beginDate: node.beginDate,
		endDate: node.endDate,
		isActive: node.isActive
	})
});

export const ContractsList = () => {
	const { t } = useTranslation('contracts');
	const apolloClient = useApolloClient();
	const location = useLocation();
	const navigate = useNavigate();
	const { goToCreate, goToEdit } = useResourceNavigation('contracts');
	const go = useGo();
	const { showDialog } = useConfirmationDialog();

	// Track total items to fetch (for preserving count after create/edit refetch)
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);

	const { data, loading, error, refetch, fetchMore } =
		useQuery<ContractsListQueryResponse>(CONTRACTS_LIST_QUERY, {
			variables: {
				first: itemsToFetch
			},
			fetchPolicy: 'cache-and-network',
			errorPolicy: 'all'
		});

	// Pagination state
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const hasNextPage = data?.contracts?.pageInfo?.hasNextPage ?? false;
	const endCursor = data?.contracts?.pageInfo?.endCursor;

	// Load more handler using fetchMore
	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			const result = await fetchMore({
				variables: {
					first: DEFAULT_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.contracts) return prev;

					return {
						...prev,
						contracts: {
							...fetchMoreResult.contracts,
							nodes: [
								...(prev.contracts?.nodes ?? []),
								...(fetchMoreResult.contracts.nodes ?? [])
							]
						}
					};
				}
			});
			// Update items to fetch count to preserve loaded items on refetch
			const newItemsCount = result.data?.contracts?.nodes?.length ?? 0;
			if (newItemsCount > 0) {
				setItemsToFetch(prev => prev + DEFAULT_PAGE_SIZE);
			}
			// Reset local contracts to use server data after fetchMore
			setLocalContracts([]);
		} catch (error) {
			console.error('Error loading more contracts:', error);
		} finally {
			setIsLoadingMore(false);
		}
	}, [fetchMore, hasNextPage, isLoadingMore, endCursor]);

	const [deleteContract, { loading: deleteLoading }] = useMutation(
		DELETE_CONTRACT_MUTATION
	);

	// Local state to manage contract list data with imperative updates
	const [localContracts, setLocalContracts] = useState<ContractListItem[]>(
		[]
	);

	const handleRefetch = () => {
		setLocalContracts([]);
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		refetch({ first: DEFAULT_PAGE_SIZE });
	};

	useEffect(() => {
		if (error) {
			console.warn('Contracts query failed:', error);
		}
	}, [error]);

	const contracts = useMemo(() => {
		const nodes = data?.contracts?.nodes ?? [];
		const serverContracts = nodes
			.filter(Boolean)
			.map(node => {
				try {
					return mapNodeToListItem(node);
				} catch (mappingError) {
					console.warn('Unable to map contract node', mappingError);
					return null;
				}
			})
			.filter((item): item is ContractListItem => item !== null);

		// Always prefer server data when empty - local state is only for optimistic updates
		if (localContracts.length === 0 || serverContracts.length === 0) {
			return serverContracts;
		}
		return localContracts;
	}, [data?.contracts?.nodes, localContracts]);

	// Update local contracts when query data changes (but only if no local override exists)
	useEffect(() => {
		if (!loading && data?.contracts?.nodes && localContracts.length === 0) {
			const nodes = data.contracts.nodes;
			const mappedContracts = nodes
				.filter(Boolean)
				.map(node => {
					try {
						return mapNodeToListItem(node);
					} catch (mappingError) {
						console.warn(
							'Unable to map contract node',
							mappingError
						);
						return null;
					}
				})
				.filter((item): item is ContractListItem => item !== null);
			setLocalContracts(mappedContracts);
		}
	}, [data?.contracts?.nodes, loading, localContracts.length]);

	const [selectedContract, setSelectedContract] =
		useState<ContractListItem | null>(
			contracts.length > 0 ? contracts[0] : null
		);

	useEffect(() => {
		if (contracts.length === 0) {
			setSelectedContract(null);
			return;
		}

		if (!selectedContract) {
			setSelectedContract(contracts[0]);
			return;
		}

		const updatedSelection = contracts.find(
			(contract: ContractListItem) =>
				contract.contractId === selectedContract.contractId
		);

		if (!updatedSelection) {
			setSelectedContract(contracts[0]);
			return;
		}

		if (updatedSelection !== selectedContract) {
			setSelectedContract(updatedSelection);
		}
	}, [contracts, selectedContract]);

	// Handle contract selection from location state (after create/edit)
	useEffect(() => {
		const state = location.state as { selectContractId?: string } | null;
		if (state?.selectContractId && contracts.length > 0 && !loading) {
			const contractIdToSelect = state.selectContractId;
			// Trigger the selection after a short delay to ensure grid is ready
			const timeoutId = setTimeout(async () => {
				try {
					await selectContractAfterSave({
						contractId: contractIdToSelect,
						client: apolloClient,
						currentContracts: contracts,
						onDataUpdate: setLocalContracts,
						onContractSelect: setSelectedContract,
						mapNodeToListItem
					});
					// Clear the location state after processing to prevent re-triggering
					navigate(location.pathname, { replace: true, state: null });
				} catch (error) {
					console.error(
						'Failed to select contract after navigation:',
						error
					);
				}
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [
		location.state,
		contracts.length,
		loading,
		apolloClient,
		contracts,
		navigate,
		location.pathname
	]);

	const getCustomerDisplayName = (customer: ContractListItem['customer']) => {
		if (!customer) return 'Unknown customer';

		// For companies, show firm name
		if (customer.isCompany) {
			return customer.firmName || 'Unknown customer';
		}

		// For individuals, show first name + last name
		const firstName = customer.firstName || '';
		const lastName = customer.lastName || '';

		if (firstName || lastName) {
			return `${firstName} ${lastName}`.trim();
		}

		// Fallback to firm name if no first/last name
		return customer.firmName || 'Unknown customer';
	};

	// Helper function to find the next contract to select after deletion
	const findNextContractToSelect = (
		deletedContract: ContractListItem,
		contractList: ContractListItem[]
	) => {
		const currentIndex = contractList.findIndex(
			c => c.contractId === deletedContract.contractId
		);

		if (contractList.length <= 1) {
			return null;
		}

		// Try to select the next contract (same index after deletion)
		if (currentIndex < contractList.length - 1) {
			return contractList[currentIndex + 1];
		}

		// If deleting the last contract, select the previous one
		if (currentIndex > 0) {
			return contractList[currentIndex - 1];
		}

		return null;
	};

	// Helper function to handle post-deletion selection
	const handlePostDeletionSelection = (
		nextContractToSelect: ContractListItem | null,
		updatedContracts: ContractListItem[]
	) => {
		setLocalContracts([]);
		// First, clear the current selection completely
		setSelectedContract(null);

		// Then after a brief delay, set the next contract
		setTimeout(() => {
			if (nextContractToSelect && updatedContracts.length > 0) {
				// Find the contract in the updated list by ID
				const updatedContract = updatedContracts.find(
					c => c.contractId === nextContractToSelect?.contractId
				);

				if (updatedContract) {
					setSelectedContract(updatedContract);
				}
			}
		}, 100);
	};

	const handleDeleteContract = async (contract: ContractListItem) => {
		const customerName = getCustomerDisplayName(contract.customer);
		const confirmed = await showDialog(
			'Delete Contract',
			`Are you sure you want to delete contract for "${customerName}"? This action cannot be undone.`
		);

		if (confirmed) {
			// Clear selection immediately if deleting the selected contract
			if (selectedContract?.contractId === contract.contractId) {
				setSelectedContract(null);
			}

			try {
				const result = await deleteContract({
					variables: {
						input: {
							contractId: contract.contractId
						}
					},
					refetchQueries: ['ContractsList']
				});

				const checkResult = result.data?.deleteContract;

				if (checkResult?.success) {
					showMutationSuccessToast('contracts', 'delete');

					// Find the next contract to select after deletion (before refetch)
					let nextContractToSelect = null;
					if (selectedContract?.contractId === contract.contractId) {
						nextContractToSelect = findNextContractToSelect(
							contract,
							contracts
						);
					}

					// Refetch data
					refetch().then(result => {
						const updatedContracts =
							result.data?.contracts?.nodes ?? [];
						const mappedContracts = updatedContracts
							.filter(Boolean)
							.map(node => {
								try {
									return mapNodeToListItem(node);
								} catch (mappingError) {
									console.warn(
										'Unable to map contract node',
										mappingError
									);
									return null;
								}
							})
							.filter(
								(item): item is ContractListItem =>
									item !== null
							);
						handlePostDeletionSelection(
							nextContractToSelect,
							mappedContracts
						);
					});
				} else {
					toast.error(
						checkResult?.message || 'Failed to delete contract'
					);
				}
			} catch (error) {
				console.error('Error deleting contract:', error);
				toast.error('Error deleting contract. Please try again.');
			}
		}
	};

	const handleDeleteContractById = async (contractId: string) => {
		const contract = contracts.find(c => c.contractId === contractId);
		if (contract) {
			await handleDeleteContract(contract);
		}
	};

	const handleCloneContract = (contractId: string) => {
		const contract = contracts.find(c => c.contractId === contractId);
		if (contract) {
			// Map contract vehicles for cloning (explicit vehicles only, no rfidTags or isVip any more)
			const clonedVehicles = contract.vehicles.map(vehicle => ({
				vehicleId: vehicle.vehicleId,
				plateNumber: vehicle.plateNumber
			}));

			// Map VIP vehicles for cloning
			const clonedVipVehicleIds = contract.vipVehicleIds;

			// Compute customer display label (same logic as column valueGetter)
			let customerLabel = '';
			const customer = contract.customer;
			if (customer) {
				if (customer.isCompany) {
					customerLabel = customer.firmName || '';
				} else {
					const firstName = customer.firstName || '';
					const lastName = customer.lastName || '';
					customerLabel = `${firstName} ${lastName}`.trim() || customer.firmName || '';
				}
			}

			go({
				to: '/contracts/create',
				state: {
					cloneData: {
						// Core contract fields
						customerId: contract.customer?.customerId ?? '',
						customerLabel,
						beginDate: contract.beginDate,
						endDate: contract.endDate,
						amount: contract.amount?.toString() ?? '',
						isAutoRenewed: contract.isAutoRenewed,
						simultaneousVehicleLimit: contract.simultaneousVehicleLimit,
						reservedParkingSlots: contract.reservedParkingSlots,
						isActive: contract.isActive,
						preferredLanguage: contract.preferredLanguage,
						includeAllCustomerVehicles: contract.includeAllCustomerVehicles,
						// Related entities
						vehicles: clonedVehicles,
						vipVehicleIds: clonedVipVehicleIds
						// NOTE: cards are not cloned (card keys are unique)
					}
				}
			});
		}
	};

	const columns: ColDef<ContractListItem>[] = [
		{
			headerName: t('list.columns.customer'),
			field: 'customer.firmName',
			flex: 1,
			minWidth: 100,
			width: 200,
			valueGetter: params => {
				const customer = params.data?.customer;
				if (!customer) return '—';

				// For companies, show firm name
				if (customer.isCompany) {
					return customer.firmName || '—';
				}

				// For individuals, show first name + last name
				const firstName = customer.firstName || '';
				const lastName = customer.lastName || '';

				if (firstName || lastName) {
					return `${firstName} ${lastName}`.trim();
				}

				// Fallback to firm name if no first/last name
				return customer.firmName || '—';
			},
			cellRenderer: (params: any) => {
				const customer = params.data?.customer;
				const contractNumber = params.data?.contractNumber;
				if (!customer) return '—';

				let displayName = '—';
				if (customer.isCompany) {
					displayName = customer.firmName || '—';
				} else {
					const firstName = customer.firstName || '';
					const lastName = customer.lastName || '';
					displayName = (firstName || lastName)
						? `${firstName} ${lastName}`.trim()
						: customer.firmName || '—';
				}

				return (
					<div className="flex flex-col py-1 overflow-hidden min-w-0 w-full">
						<div className="font-medium truncate">{displayName}</div>
						{contractNumber && (
							<div className="text-xs text-muted-foreground font-mono truncate">
								{contractNumber}
							</div>
						)}
					</div>
				);
			}
		},
		{
			headerName: t('list.columns.term'),
			minWidth: 20,
			width: 200,
			field: 'beginDate',
			valueGetter: params =>
				formatDateRange(params.data?.beginDate, params.data?.endDate)
		},
		{
			headerName: t('list.columns.amount'),
			field: 'amount',
			minWidth: 20,
			width: 130,
			valueGetter: params => formatCurrency(params.data?.amount ?? null)
		},
		{
			headerName: t('list.columns.vehicleLimit'),
			field: 'simultaneousVehicleLimit',
			minWidth: 20,
			width: 150,
			valueGetter: params => {
				const record = params.data;
				if (!record) {
					return '—';
				}

				if (record.simultaneousVehicleLimit != null) {
					return `${record.simultaneousVehicleLimit} vehicles`;
				}

				if (record.customer.categoryLimit != null) {
					return `Category: ${record.customer.categoryLimit}`;
				}

				return 'Not set';
			}
		},
		{
			headerName: t('list.columns.status'),
			field: 'isActive',
			minWidth: 20,
			width: 120,
			cellRenderer: ActiveInactiveCellRenderer,
			sortable: true,
			filter: true
		},
		...(showDeleteColumn ? [{
			headerName: '',
			field: undefined,
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			cellRenderer: DeleteCellRenderer,
			cellRendererParams: {
				onDelete: handleDeleteContract
			},
			sortable: false,
			filter: false,
			resizable: false
		}] : [])
	];

	return (
		<ListPageLayout
			gridId="contracts-list"
			columns={columns}
			data={contracts}
			title={t('list.title')}
			description={t('list.description')}
			searchPlaceholder={t('list.searchPlaceholder')}
			isLoading={loading || deleteLoading}
			onRowClick={row => setSelectedContract(row)}
			onCreate={goToCreate}
			onRefresh={handleRefetch}
			selectedItem={selectedContract}
			detailsTitle="Contract details"
			onEdit={id => goToEdit(id)}
			onDelete={handleDeleteContractById}
			onClone={handleCloneContract}
			onOpenInNewWindow={id => openEntityInNewWindow('contracts', id)}
			renderDetailsContent={item => (
				<ContractDetailsQuery contractId={item.contractId} />
			)}
			renderDetailsHeader={(item, actions) => (
				<ContractDetailsHeader contract={item} {...actions} />
			)}
			getRecordId={item => item.contractId}
			hasNextPage={hasNextPage}
			isLoadingMore={isLoadingMore}
			onLoadMore={handleLoadMore}
			entityName={t('entityName')}
		/>
	);
};
